import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ActivityFeed } from '../../components/portal/ActivityFeed';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';
interface ProjectsProps {
  /** The New Project form is opened from the header. */
  creating: boolean;
  setCreating: (open: boolean) => void;
}
export function Projects({
  creating,
  setCreating
}: ProjectsProps) {
  const {
    activeProjects,
    archivedProjects,
    templates,
    openProject,
    createProject,
    reopenProject,
    notify
  } = usePortal();
  const [client, setClient] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client.trim() || !name.trim()) return;
    createProject({
      client: client.trim(),
      name: name.trim(),
      type: type.trim(),
      templateId
    });
    notify(`${client.trim()} project created`);
    setClient('');
    setName('');
    setType('');
    setTemplateId('');
    setCreating(false);
  }

  /* The archive: the same list, kept off the working dashboard. */
  if (showArchive) {
    return <div data-id="element-775" data-mp="27z7ha5qhdi17">
        <button type="button" onClick={() => setShowArchive(false)} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-776" data-mp="27zh9lzz001a2">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-777" data-mp="2gyuiaqfasltq" />
          Client Projects
        </button>

        <h1 className="mt-10 border-b border-ruleDark pb-8 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-778" data-mp="1p87xdixueyes">
          Archived Projects
        </h1>

        <ul className="m-0 list-none p-0" data-id="element-779" data-mp="3tbspkr1bojvw">
          {archivedProjects.map(row => <li key={row.id} className="grid grid-cols-1 items-baseline gap-x-8 gap-y-4 border-b border-ruleDark py-8 sm:grid-cols-12" data-id="element-780" data-mp="1xd6stzqnb9c">
              <span className="font-display text-2xl leading-none sm:col-span-5" data-id="element-781" data-mp="6yua0w5p39t3">{row.client}</span>
              <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-782" data-mp="3adq6stzdbj6j">{row.name}</span>
              <span className="flex flex-wrap items-center gap-4 sm:col-span-3 sm:justify-end" data-id="element-783" data-mp="3afjco725pzdh">
                <button type="button" onClick={() => openProject(row.id)} className={BUTTON_QUIET} data-id="element-784" data-mp="3ff5rgkxmvnoe">
                  Open Project
                </button>
                <button type="button" onClick={() => {
              reopenProject(row.id);
              notify(`${row.client} restored to Active Projects`);
            }} className={BUTTON_QUIET} data-id="element-785" data-mp="zl5g2mc2m2xa">
                  Restore Project
                </button>
              </span>
            </li>)}
          {archivedProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-786" data-mp="25ea0es2n44rm">
              Nothing archived.
            </li>}
        </ul>

        <p className="label-sm mt-8 max-w-xl leading-relaxed text-paper/30" data-id="element-787" data-mp="1wdvdxpdxwjmj">
          Nothing is deleted — deliverables, comments, approvals, version history, project
          information and the client link are all retained.
        </p>
      </div>;
  }
  return <div data-id="element-788" data-mp="3lt1o4roucnul">
      {creating && <form onSubmit={submit} className="mb-12 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-789" data-mp="3ls0808syxb60">
          <h2 className="font-display text-2xl leading-none" data-id="element-790" data-mp="3gtozq2ibn8pw">New Project</h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-791" data-mp="2egtui22ea8d8">
            <label className="block" data-id="element-792" data-mp="2eeg3c4au5gay">
              <span className="label block text-paper/40" data-id="element-793" data-mp="2iinojri34e4f">Client</span>
              <input value={client} onChange={event => setClient(event.target.value)} placeholder="e.g. Example Group" autoFocus className={FIELD} data-id="element-794" data-mp="24iczmu0c59lw" />
            </label>
            <label className="block" data-id="element-795" data-mp="1ur09c6u6qrhz">
              <span className="label block text-paper/40" data-id="element-796" data-mp="1zj9u78gg11de">Project Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Leadership Series" className={FIELD} data-id="element-797" data-mp="1zle5wckl34o8" />
            </label>
            <label className="block" data-id="element-798" data-mp="1bt74gz0047hy">
              <span className="label block text-paper/40" data-id="element-799" data-mp="1bu8ss5612qhm">Project Type</span>
              <input value={type} onChange={event => setType(event.target.value)} placeholder="e.g. Video Series" className={FIELD} data-id="element-800" data-mp="1grqr71mf5nbd" />
            </label>
            <label className="block" data-id="element-801" data-mp="t77xpvhvkskz">
              <span className="label block text-paper/40" data-id="element-802" data-mp="174wm3db5ihus">Start From (optional)</span>
              <select value={templateId} onChange={event => setTemplateId(event.target.value)} className={SELECT} data-id="element-803" data-mp="12635vd4hccqx">
                <option value="" className={OPTION} data-id="element-804" data-mp="1g3rr4m2gllwi">
                  Blank project
                </option>
                {templates.map(template => <option key={template.id} value={template.id} className={OPTION} data-id="element-805" data-mp="1pumclo7vshgw">
                    {template.name}
                  </option>)}
              </select>
            </label>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-806" data-mp="1zud7hr0csyhi">
            <button type="submit" className={BUTTON_SOLID} data-id="element-807" data-mp="1uvjvk30ptfjg">
              Create Project
            </button>
            <button type="button" onClick={() => setCreating(false)} className={BUTTON_QUIET} data-id="element-808" data-mp="21z03yfcx62xi">
              Cancel
            </button>
          </div>
          <p className="label-sm mt-8 max-w-lg leading-relaxed text-paper/30" data-id="element-809" data-mp="26upbb4v6ko4d">
            Templates are starting structures only — every deliverable can be added, renamed,
            reordered or removed afterwards.
          </p>
        </form>}

      <NeedsAttention data-id="element-810" data-mp="2ll69mfciqxx1" />

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-811" data-mp="2lo0si2fvwaoc">
        <section aria-labelledby="active-projects" className="lg:col-span-8" data-id="element-812" data-mp="2plyhb45qsur7">
          <h2 id="active-projects" className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-813" data-mp="2unnuoqgftkig">
            Active Projects
          </h2>
          <ul className="m-0 list-none p-0" data-id="element-814" data-mp="108v3r4qi1hwu">
            {activeProjects.map(row => {
            const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
            const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
            return <li key={row.id} className="border-b border-ruleDark" data-id="element-815" data-mp="izj1rcesa79z">
                  <button type="button" onClick={() => openProject(row.id)} className="group grid w-full grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-816" data-mp="ix6csfalywnf">
                    <span className="font-display text-3xl leading-none sm:col-span-5 sm:text-4xl" data-id="element-817" data-mp="16nk4g4pa6thh">
                      {row.client}
                    </span>
                    <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-818" data-mp="3reue88g7o08k">
                      {row.name}
                    </span>
                    <span className="label text-paper/40 sm:col-span-1" data-id="element-819" data-mp="3meg3ujr50e7d">
                      {row.clientUsers.length} user{row.clientUsers.length === 1 ? '' : 's'}
                    </span>
                    <span className="label flex items-center justify-between gap-4 text-paper sm:col-span-2 sm:justify-end" data-id="element-820" data-mp="kyn2m4xhxga1">
                      {changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row)}
                      <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-821" data-mp="b1qs5to1ek2u" />
                    </span>
                  </button>
                </li>;
          })}
            {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-822" data-mp="tw5v1udgywvy">
                No active projects.
              </li>}
          </ul>

          {archivedProjects.length > 0 && <button type="button" onClick={() => setShowArchive(true)} className="label-sm mt-10 text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-823" data-mp="374q8b76os355">
              Archived Projects · {archivedProjects.length}
            </button>}
        </section>

        <div className="lg:col-span-4 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-824" data-mp="3byazogdrxyvd">
          <ActivityFeed data-id="element-825" data-mp="2o6mbnc7zjwk8" />
        </div>
      </div>
    </div>;
}